/**
 * Dashboard list row. Shows the estimate's `room_label` (or
 * `room_type` fallback, or ULID-suffix fallback) as the primary
 * identifier. Pencil icon opens the IdentityEditor inline.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import type { Estimate } from "@starz/shared/api/types.gen";
import { ROOM_TYPE_LABEL } from "@starz/shared/api/types.gen";
import { IdentityEditor } from "./IdentityEditor";

interface Props {
  estimate: Estimate;
  onUpdated: (updated: Estimate) => void;
  /** Optional — called after a successful soft-delete so the list row
   * can disappear. Mobile parity (Phase 17 follow-up). */
  onDeleted?: (estimateId: string) => void;
}

function primaryLabel(estimate: Estimate): string {
  if (estimate.room_label) return estimate.room_label;
  if (estimate.room_type) return ROOM_TYPE_LABEL[estimate.room_type];
  return `Estimate ${estimate.estimate_id.slice(-6).toUpperCase()}`;
}

export function EstimateListItem({ estimate, onUpdated, onDeleted }: Props) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <div className="rounded-xl border border-starz-gold/30 bg-white px-5 py-4 shadow-sm">
        <IdentityEditor
          estimate={estimate}
          compact
          onCancel={() => setEditing(false)}
          onSaved={(updated) => {
            setEditing(false);
            onUpdated(updated);
          }}
          onDeleted={
            onDeleted
              ? () => {
                  setEditing(false);
                  onDeleted(estimate.estimate_id);
                }
              : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-starz-gold/15 bg-white px-5 py-4 transition-colors hover:border-starz-gold/40 hover:bg-starz-cream/40">
      <Link
        to={`/estimates/${estimate.estimate_id}`}
        className="flex-1 truncate"
        aria-label={`Open ${primaryLabel(estimate)}`}
      >
        <div className="flex items-baseline justify-between gap-4">
          <span className="truncate text-base font-medium text-starz-navy">
            {primaryLabel(estimate)}
          </span>
          <span className="text-xs uppercase tracking-widest text-starz-gold">
            {estimate.status.replace("_", " ")}
          </span>
        </div>
        <div className="mt-1 text-sm text-warm-gray">
          {estimate.room_label && estimate.room_type
            ? `${ROOM_TYPE_LABEL[estimate.room_type]} · `
            : ""}
          Last activity {new Date(estimate.last_message_at).toLocaleString()}
          <span className="ml-2 text-xs text-warm-gray/60">
            ID {estimate.estimate_id.slice(-6).toUpperCase()}
          </span>
        </div>
      </Link>
      <button
        type="button"
        onClick={() => setEditing(true)}
        aria-label="Rename this estimate"
        className="ml-2 grid h-11 w-11 place-items-center rounded-md text-starz-charcoal hover:bg-starz-cream"
      >
        <svg
          aria-hidden="true"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
      </button>
    </div>
  );
}
